import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {Service} from "crqlar-react-common/dist/models/service/service.model";
import type {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import {useAllServiceCategories} from "crqlar-react-common/dist/utils/hooks/services/useAllServiceCategories";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarPanelButton} from "../../layout/rightSidebar/RightSidebarRail";
import type {ReservationServiceSelection} from "./useReservationServiceSelection";

const UNCATEGORIZED_GROUP_ID = "__uncategorized__";

type SortDirection = "asc" | "desc";

interface ServiceOption {
    id: string;
    label: string;
    service: Service;
}

interface ServiceGroup {
    id: string;
    label: string;
    services: ServiceOption[];
}

interface ReservationServicePickerProps {
    selection: ReservationServiceSelection;
}

const getLabel = (
    item: Pick<Service | ServiceCategory, "name" | "nameLocalized">,
    locale: string,
    fallback: string,
) => getLocalizedValue(locale, item.nameLocalized) || item.name || fallback;

const buildGroups = ({
    services,
    categories,
    locale,
    sortDirection,
    unnamedLabel,
    uncategorizedLabel,
}: {
    services: Service[];
    categories: ServiceCategory[];
    locale: string;
    sortDirection: SortDirection;
    unnamedLabel: string;
    uncategorizedLabel: string;
}): ServiceGroup[] => {
    const collator = new Intl.Collator(locale, {numeric: true, sensitivity: "base"});
    const direction = sortDirection === "asc" ? 1 : -1;
    const options = services
        .filter((service): service is Service & {id: string} => Boolean(service.id))
        .map(service => ({
            id: service.id,
            label: getLabel(service, locale, unnamedLabel),
            service,
        }))
        .sort((left, right) => direction * collator.compare(left.label, right.label));
    const groups = categories
        .filter((category): category is ServiceCategory & {id: string} => Boolean(category.id))
        .map(category => ({
            id: category.id,
            label: getLabel(category, locale, unnamedLabel),
            services: options.filter(option => option.service.categoryIds?.includes(category.id)),
        }))
        .filter(group => group.services.length > 0)
        .sort((left, right) => direction * collator.compare(left.label, right.label));
    const groupedIds = new Set(groups.flatMap(group => group.services.map(service => service.id)));
    const uncategorized = options.filter(option => !groupedIds.has(option.id));

    if (uncategorized.length) {
        groups.push({id: UNCATEGORIZED_GROUP_ID, label: uncategorizedLabel, services: uncategorized});
    }

    return groups;
};

const getGroupSelection = (group: ServiceGroup, selectedIds: ReadonlySet<string>) => {
    const ids = Array.from(new Set(group.services.map(service => service.id)));
    const selectedCount = ids.filter(id => selectedIds.has(id)).length;

    return {
        ids,
        all: ids.length > 0 && selectedCount === ids.length,
        some: selectedCount > 0 && selectedCount < ids.length,
    };
};

const rowSx = (selected: boolean) => ({
    m: 0,
    px: 0.75,
    py: 0.5,
    width: "100%",
    minWidth: 0,
    borderRadius: 1,
    bgcolor: selected ? "action.selected" : "transparent",
    "&:hover": {bgcolor: "action.hover"},
    "& .MuiFormControlLabel-label": {minWidth: 0},
});

export function ReservationServicePicker({selection}: ReservationServicePickerProps) {
    const {t, i18n} = useTranslation();
    const {
        services,
        selectedIds,
        selectedIdSet,
        allSelected,
        toggleAll,
        toggleGroup,
        toggleService,
    } = selection;
    const categories = useAllServiceCategories();
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
    const locale = i18n.resolvedLanguage || i18n.language;
    const groupInput = useMemo(() => ({
        services,
        categories,
        locale,
        sortDirection,
        unnamedLabel: t("unnamed"),
        uncategorizedLabel: t("Uncategorized"),
    }), [categories, locale, services, sortDirection, t]);
    const groups = useMemo(() => buildGroups(groupInput), [groupInput]);
    const groupSelections = groups.length > 1
        ? groups.map(group => ({group, ...getGroupSelection(group, selectedIdSet)}))
        : [];
    const showGroupHeaders = groups.length > 1
        || groups.some(group => group.id !== UNCATEGORIZED_GROUP_ID);

    const renderQuickRow = (
        key: string,
        label: string,
        checked: boolean,
        indeterminate: boolean,
        onToggle: () => void,
    ) => (
        <FormControlLabel
            key={key}
            control={(
                <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={onToggle}
                    size="small"
                    sx={{p: 0.5, mr: 0.5}}
                />
            )}
            label={<Typography noWrap>{label}</Typography>}
            sx={rowSx(checked || indeterminate)}
        />
    );

    const renderServiceLabel = (option: ServiceOption) => (
        <Stack minWidth={0}>
            <Typography noWrap>{option.label}</Typography>
            {option.service.managedExternally && (
                <Typography variant="caption" color="text.secondary" noWrap>
                    {t("Managed externally")}
                </Typography>
            )}
        </Stack>
    );

    return (
        <RightSidebarPanelButton
            icon={(
                <Badge
                    badgeContent={selectedIds.length}
                    showZero
                    invisible={services.length === 0}
                >
                    <RoomServiceOutlinedIcon/>
                </Badge>
            )}
            label={t("Services")}
            disabled={services.length === 0}
            renderPanel={close => (
                <RightSidebarFloatingPanel sx={{overflow: "hidden"}}>
                    <RightSidebarPanelHeader title={t("Services")} onClose={close}/>

                    <Stack sx={{px: 2, pb: 1.5, gap: 1.25, flexShrink: 0}}>
                        <Typography variant="body2" fontWeight={700} sx={{px: 0.75, py: 0.5}}>
                            {t("Filter by services")}
                        </Typography>

                        <Stack gap={0.25}>
                            {renderQuickRow("all", t("All services"), allSelected, false, toggleAll)}
                            {groupSelections.map(({group, ids, all, some}) => renderQuickRow(
                                `group-${group.id}`,
                                group.label,
                                all,
                                some,
                                () => toggleGroup(ids),
                            ))}
                        </Stack>
                    </Stack>

                    <Stack sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2}}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{py: 1, flexShrink: 0}}
                        >
                            <Typography variant="body2" fontWeight={700} sx={{px: 0.75, py: 0.5}}>
                                {t("All services")}
                            </Typography>
                            <Button
                                type="button"
                                size="small"
                                color="info"
                                endIcon={<ArrowDropDownIcon/>}
                                onClick={event => setSortAnchorEl(event.currentTarget)}
                                sx={{height: 36, minWidth: 0, whiteSpace: "nowrap"}}
                            >
                                {sortDirection === "asc" ? "A–Z" : "Z–A"}
                            </Button>
                            <Menu
                                anchorEl={sortAnchorEl}
                                open={Boolean(sortAnchorEl)}
                                onClose={() => setSortAnchorEl(null)}
                                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "right"}}
                            >
                                <MenuItem
                                    selected={sortDirection === "asc"}
                                    onClick={() => {
                                        setSortDirection("asc");
                                        setSortAnchorEl(null);
                                    }}
                                >
                                    A–Z
                                </MenuItem>
                                <MenuItem
                                    selected={sortDirection === "desc"}
                                    onClick={() => {
                                        setSortDirection("desc");
                                        setSortAnchorEl(null);
                                    }}
                                >
                                    Z–A
                                </MenuItem>
                            </Menu>
                        </Stack>

                        {groups.length === 0 ? (
                            <Typography color="text.secondary" sx={{py: 2}}>
                                {t("No services")}
                            </Typography>
                        ) : (
                            <Stack gap={1.25}>
                                {groups.map(group => (
                                    <Stack key={group.id} gap={0.25}>
                                        {showGroupHeaders && (
                                            <Typography variant="body2" fontWeight={700} sx={{px: 0.75, py: 0.5}}>
                                                {group.label}
                                            </Typography>
                                        )}
                                        {group.services.map(option => {
                                            const selected = selectedIdSet.has(option.id);

                                            return (
                                                <FormControlLabel
                                                    key={`${group.id}-${option.id}`}
                                                    control={(
                                                        <Checkbox
                                                            checked={selected}
                                                            onChange={() => toggleService(option.id)}
                                                            size="small"
                                                            sx={{p: 0.5, mr: 0.5}}
                                                        />
                                                    )}
                                                    label={renderServiceLabel(option)}
                                                    sx={rowSx(selected)}
                                                />
                                            );
                                        })}
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </RightSidebarFloatingPanel>
            )}
        />
    );
}
